import { computed, makeObservable } from 'mobx';

import { ValueModel } from '@/models/ValueModel';
import { isColorTooLight } from '@/utils/colorConversions';
import { generateColorFromPrefix } from '@/utils/getColorByPrefix';

import { ColorMapping } from './type';

const STORAGE_KEY = 'queueColors';

class ColorStore {
  private readonly _colorMapping = new ValueModel<ColorMapping>({});

  constructor() {
    makeObservable(this, {
      colorMapping: computed,
    });

    this.loadFromStorage();
  }

  get colorMapping() {
    return this._colorMapping.value;
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (!stored) {
        this._colorMapping.change({});

        return;
      }

      const mapping = JSON.parse(stored) as ColorMapping;
      this._colorMapping.change(mapping);
    } catch {
      this._colorMapping.change({});
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.colorMapping));
    } catch (error) {
      console.error('Failed to save color mapping:', error);
    }
  }

  getColor(queue: string): string | undefined {
    return this.colorMapping[queue];
  }

  getColorByPrefix(prefix: string): string {
    const mapping = this.colorMapping;

    if (mapping[prefix]) {
      return mapping[prefix];
    }

    return generateColorFromPrefix(prefix);
  }

  needsBorder(prefix: string): boolean {
    const color = this.getColorByPrefix(prefix);

    return isColorTooLight(color);
  }

  updateQueueColor(queue: string, color: string): void {
    const newMapping = { ...this.colorMapping };
    newMapping[queue] = color;
    this._colorMapping.change(newMapping);
    this.saveToStorage();
  }

  resetQueueColor(queue: string): void {
    const newMapping = { ...this.colorMapping };
    delete newMapping[queue];
    this._colorMapping.change(newMapping);
    this.saveToStorage();
  }

  clearAllColors(): void {
    this._colorMapping.reset();
    this.saveToStorage();
  }
}

export const colorStore = new ColorStore();
